// eslint-disable-next-line import/no-cycle
import bffApi from '@client/bffApi';
import format from 'date-fns/format';
import {DATE_FORMAT_DB} from '@client/util/constants';
import parseISO from 'date-fns/parseISO';
import {ClUsersWorkflow, OrchestrateService} from '@simpleinvest/ms-orchestrate-sdk';
import {apiKey, getLogger} from '@client/services/sdk';
import {
  ClientService,
  PostOnboardingDataOptions,
  GetOnboardingDataOptions,
  OnboardingResponse,
} from '@simpleinvest/ms-client-sdk';

// eslint-disable-next-line import/no-cycle
import {
  requestInterceptorFulfilledHandler,
  requestInterceptorRejectedHandler,
  responseInterceptorFulfilledHandler,
  responseInterceptorRejectedHandler,
} from '@client/interceptors';
import {getUserId} from '@simpleinvest/front-end-auth';
import {SourcePlatform} from '@simpleinvest/ms-core-sdk';

export interface VerificationInfo {
  clUserId: number;
  firstName: string;
  middleName?: string;
  lastName: string;
  email: string;
  emailVerified: string;
  workflowDocId: number;
}

export interface GetVerificationInfoResponse {
  data: VerificationInfo;
  requestId: string;
}

export const getVerificationInfoFromVerificationCode = async (
  riaId: string,
  email: string,
  emailVerificationCode: string
): Promise<VerificationInfo> => {
  const {data: response} = await bffApi
    .getAxiosInstance()
    .get<GetVerificationInfoResponse>(
      `${bffApi.getBaseUrl()}/v1/onboarding/rias/${riaId}/email/${email}/${emailVerificationCode}/client`
    );

  const {data: verificationInfo} = response;

  return verificationInfo;
};

export interface EnrollClientInitiatedOptions {
  riaId: string;
  cdoParameter: string;
  ssn: string;
  dob: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  password: string;
  email: string;
}

export const enrollClientInitiated = async (options: EnrollClientInitiatedOptions): Promise<void> => {
  await bffApi
    .getAxiosInstance()
    .post<void>(`${bffApi.getBaseUrl(true)}/client-onboarding/firms/${options.riaId}/${options.cdoParameter}/enroll`, {
      ssn: options.ssn,
      dob: format(parseISO(options.dob), DATE_FORMAT_DB),
      firstName: options.firstName,
      middleName: options.middleName,
      lastName: options.lastName,
      password: options.password,
      email: options.email,
    });
};

export interface VerifyOnboardingEmailOptions {
  riaId: string;
  email: string;
  emailVerificationCode: string;
}

export const verifyOnboardingEmail = async (options: VerifyOnboardingEmailOptions): Promise<void> => {
  await bffApi
    .getAxiosInstance()
    .post<void>(`${bffApi.getBaseUrl()}/v1/rias/${options.riaId}/client/onboarding/verify-email`, {
      email: options.email,
      emailVerificationCode: options.emailVerificationCode,
    });
};

export default {
  getClientServiceInstance(): ClientService {
    return new ClientService({
      apiKey,
      sourceInfo: {platform: SourcePlatform.ClientPlatform},
      interceptors: {
        requestInterceptor: requestInterceptorFulfilledHandler,
        requestRejectedInterceptor: requestInterceptorRejectedHandler,
        responseInterceptor: responseInterceptorFulfilledHandler,
        responseRejectedInterceptor: responseInterceptorRejectedHandler,
      },
      logger: getLogger(),
    });
  },
  getOrchestrateServiceInstance(): OrchestrateService {
    return new OrchestrateService({
      apiKey,
      sourceInfo: {platform: SourcePlatform.ClientPlatform},
      interceptors: {
        requestInterceptor: requestInterceptorFulfilledHandler,
        requestRejectedInterceptor: requestInterceptorRejectedHandler,
        responseInterceptor: responseInterceptorFulfilledHandler,
        responseRejectedInterceptor: responseInterceptorRejectedHandler,
      },
      logger: getLogger(),
    });
  },
  async resendVerificationEmail(): Promise<void> {
    const service: OrchestrateService = this.getOrchestrateServiceInstance();
    const clUserId = await getUserId();

    await service.postClUsersWorkflows({
      workflows: [ClUsersWorkflow.CreateEmailVerification],
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error-error
      clUser: {
        id: Number(clUserId),
      },
      emailVerificationInfo: {
        accountRequestId: 0,
        emailTemplate: 'emailVerificationToClient',
        emailValidationUrlPath: '/clientdashboard/verify-user',
      },
    });
  },
  async saveOnboarding(options: PostOnboardingDataOptions): Promise<OnboardingResponse> {
    const service: ClientService = await this.getClientServiceInstance();

    return service.postOnboardingData(options);
  },

  async getOnboarding(options: GetOnboardingDataOptions): Promise<OnboardingResponse> {
    const service: ClientService = await this.getClientServiceInstance();

    return service.getOnboardingData(options);
  },
};
